<template>
  <div>
    <Error
      :status-code="error.statusCode"
      :maintain-start-time="error.maintainStartTime"
      :maintain-end-time="error.maintainEndTime"
      :sub-title="subTitle"
      :title="title"
      :title-tip="titleTip"
      @reload="reload"
    />
  </div>
</template>

<script>
import {
  defineComponent,
  onMounted,
  ref,
  toRef,
  useMeta,
  useRouter,
  useStore
} from '@nuxtjs/composition-api'
import Error from '../components/layouts/Error'
import useListeningToRefreshToken from '../../composables/useListeningToRefreshToken'
import useForceLogoutV2 from '../../composables/useForceLogoutV2'
import useComp from '../../composables/useComp'
import useErrorHandler from '../../composables/useErrorHandler'
import useResizeToDetectMobile from '../../composables/useResizeToDetectMobile'

// throw error example with other page:

// async fetch({ error }) {
//   error({
//     statusCode: 400,
//     message: '憑證已過期'
//   })
// }

// async fetch({ error }) {
//   error({
//     statusCode: 403,
//     message: ''
//   })
// }

// async fetch({ error }) {
//   error({
//     statusCode: 500,
//     message: `伺服器錯誤`
//   })
// }

// async fetch({ error }) {
//   error({
//     statusCode: 503,
//     message: `网站维护时间于 14:00 至 16:00<br>造成您的不便，敬请见谅`
//   })
// }

export default defineComponent({
  layout: 'nothing',
  components: { Error },
  props: {
    error: {
      type: Object,
      default: null
    }
  },
  head: {},
  setup(props, context) {
    useListeningToRefreshToken(context)
    useForceLogoutV2(context)
    useResizeToDetectMobile(context)

    const store = useStore()

    const error = toRef(props, 'error')

    const { compName } = useComp()
    useMeta(() => ({
      title: context.root.$t('layout.error.head.title', [
        store.state.httpStatusCodes[error.value.statusCode] || '',
        compName.value
      ])
    }))

    const { responseCodeToMessage } = useErrorHandler(context)

    const title = ref(store.state.httpStatusCodes[error.value.statusCode] || '')
    const subTitle = ref(
      error.value.hasOwnProperty('code') && error.value.code !== 0
        ? responseCodeToMessage(error.value.code)
        : context.root.$t(
            'component.mixin.mixinErrorHandler.method.errorHandler.other.networkError'
          )
    )
    const titleTip = ref('')

    if (error.value.statusCode === 400) {
      subTitle.value =
        subTitle.value || context.root.$t('layout.error.subTitle.400')
      titleTip.value =
        titleTip.value || context.root.$t('layout.error.titleTip.400')
    }
    if (error.value.statusCode === 403) {
      title.value = 'NOT IN SERVICE AREA'
      subTitle.value = context.root.$t('layout.error.subTitle.403')
      titleTip.value = context.root.$t('layout.error.titleTip.403')
    }
    if (error.value.statusCode === 404) {
      subTitle.value = context.root.$t('layout.error.subTitle.404')
      titleTip.value = context.root.$t('layout.error.titleTip.404')
    }
    if (
      error.value.statusCode === 502 &&
      error.value.message.indexOf('openresty') !== -1
    ) {
      error.value.statusCode = 429
    }
    if (error.value.statusCode === 429) {
      subTitle.value = 'Too many requests, please try again later.'
    }
    if (error.value.statusCode === 504) {
      error.value.statusCode = 503
    }
    if ([503, 599].includes(error.value.statusCode)) {
      title.value = 'Under construction.'
      subTitle.value = context.root.$t('layout.error.subTitle.503')
      titleTip.value = context.root.$t('layout.error.titleTip.503')
    }

    const { logging } = useErrorHandler(context)
    onMounted(() => {
      // Sky-CDN needs to reload the page
      if (error.value.message === 'Connection timeout') {
        return window.location.reload()
      }

      if (error.value.statusCode === 500) {
        logging(error.value)
      }
    })

    const router = useRouter()
    function reload() {
      if (error.value.path && error.value.path.startsWith('/')) {
        return router.push(error.value.path)
      }
      window.location.reload()
    }

    return {
      title,
      subTitle,
      titleTip,

      reload
    }
  }
})
</script>
